import { useState, useEffect, useRef } from 'react'
import { useParams, useLocation, Link } from 'react-router-dom'
import { getApiUrl } from '../config/api'
import '../App.css'

const slugify = (text = '') =>
  text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[\s\W-]+/g, '-')

function DynamicPage() {
  
  const { slug } = useParams()
  const location = useLocation()
  const [page, setPage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [contents, setContents] = useState([])
  const [filteredContents, setFilteredContents] = useState([])
  const searchResultsRef = useRef(null)
  const contentRefs = useRef({})


  // Parse text to separate English and Hindi sections
  const parsePageText = (text) => {
    if (!text) return { english: '', hindi: '' }
    
    // Try to detect Hindi text (contains Devanagari characters)
    const hindiRegex = /[\u0900-\u097F]/
    const lines = text.split('\n').filter(line => line.trim())
    
    let englishParts = []
    let hindiParts = []
    let isHindiSection = false
    
    for (const line of lines) {
      if (hindiRegex.test(line)) {
        isHindiSection = true
        hindiParts.push(line.trim())
      } else if (line.trim()) {
        if (isHindiSection && englishParts.length > 0) {
          // Hindi section started after English
          hindiParts.push(line.trim())
        } else {
          englishParts.push(line.trim())
        }
      }
    }

   
    
    
    // If no clear separation, check if text contains both
    if (englishParts.length === 0 && hindiParts.length === 0) {
      const parts = text.split(/\n\n+/)
      if (parts.length >= 2) {
        englishParts = [parts[0].trim()]
        hindiParts = [parts.slice(1).join(' ').trim()]
      } else {
        // Single paragraph - check if it has Hindi
        if (hindiRegex.test(text)) {
          hindiParts = [text.trim()]
        } else {
          englishParts = [text.trim()]
        }
      }
    }
    
    return {
      english: englishParts.join(' '),
      hindi: hindiParts.join(' ')
    }
  }

  // Handle search
  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase()
    setSearchQuery(query)

    if (query === '') {
      setFilteredContents(contents)
      return
    }

    const filtered = contents.filter((content) => (content?.title || '').toLowerCase().includes(query))
    setFilteredContents(filtered)

    // Scroll to first match
    if (filtered.length > 0) {
      setTimeout(() => {
        const firstMatchId = filtered[0].id
        const firstMatchElement = contentRefs.current[firstMatchId]
        if (firstMatchElement) {
          firstMatchElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        } else if (searchResultsRef.current) {
          searchResultsRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
        }
      }, 100)
    }
  }

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && searchQuery && filteredContents.length > 0) {
      e.preventDefault()
      setTimeout(() => {
        const firstMatchId = filteredContents[0].id
        const firstMatchElement = contentRefs.current[firstMatchId]
        if (firstMatchElement) {
          firstMatchElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          })
        } else if (searchResultsRef.current) {
          searchResultsRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          })
        }
      }, 100)
    }
  }

  // Scroll to results when search query changes
  useEffect(() => {
    if (searchQuery && filteredContents.length > 0) {
      setTimeout(() => {
        const firstMatchId = filteredContents[0].id
        const firstMatchElement = contentRefs.current[firstMatchId]
        if (firstMatchElement) {
          firstMatchElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          })
        } else if (searchResultsRef.current) {
          searchResultsRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          })
        }
      }, 100)
    }
  }, [searchQuery, filteredContents])

  // Fetch page data and contents separately
  useEffect(() => {
    const pageSlug = slug || location.pathname.slice(1) || 'home'

    if (!pageSlug || pageSlug === 'contact') {
      setLoading(false)
      return
    }

    let isMounted = true

    const updatePageMeta = (pageData) => {
      if (!pageData) return

      if (pageData.meta_title) {
        document.title = pageData.meta_title
      } else if (pageData.title) {
        document.title = `${pageData.title} - SkillSiksha`
      }

      if (pageData.meta_description) {
        let metaDesc = document.querySelector('meta[name="description"]')
        if (!metaDesc) {
          metaDesc = document.createElement('meta')
          metaDesc.setAttribute('name', 'description')
          document.getElementsByTagName('head')[0].appendChild(metaDesc)
        }
        metaDesc.setAttribute('content', pageData.meta_description)
      }

      if (pageData.meta_keywords) {
        let metaKeywords = document.querySelector('meta[name="keywords"]')
        if (!metaKeywords) {
          metaKeywords = document.createElement('meta')
          metaKeywords.setAttribute('name', 'keywords')
          document.getElementsByTagName('head')[0].appendChild(metaKeywords)
        }
        metaKeywords.setAttribute('content', pageData.meta_keywords)
      }
    }

    const fetchData = async () => {
      setError(null)
      setLoading(true)
      try {
        const pageUrl = getApiUrl(`pages/${pageSlug}`)
        const contentsUrl = getApiUrl(`pages/${pageSlug}/contents`)

        const [pageResponse, contentsResponse] = await Promise.all([
          fetch(pageUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
          }),
          fetch(contentsUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
          }),
        ])

        if (!pageResponse.ok) {
          if (pageResponse.status === 404) {
            throw new Error('Page not found')
          }
          throw new Error(`Failed to load page. Status: ${pageResponse.status}`)
        }

        const pageData = await pageResponse.json()

        let contentsData = []
        if (contentsResponse.ok) {
          const contentsJson = await contentsResponse.json()
          const rawContents = Array.isArray(contentsJson.contents) ? contentsJson.contents : []
          contentsData = rawContents.map((item) => ({
            ...item,
            slug: item?.slug || slugify(item?.title || ''),
          }))
        } else if (contentsResponse.status !== 404) {
          throw new Error(`Failed to load page contents. Status: ${contentsResponse.status}`)
        }

        if (!isMounted) return

        setPage(pageData)
        setContents(contentsData)
        setFilteredContents(contentsData)
        setLoading(false)
        updatePageMeta(pageData)
      } catch (err) {
        if (!isMounted) return
        console.error('Error fetching page data:', err)
        setError(err.message || 'Failed to load page')
        setContents([])
        setFilteredContents([])
        setLoading(false)
      }
    }

    fetchData()

    return () => {
      isMounted = false
    }
  }, [slug, location.pathname])

  const pageContent = page ? parsePageText(page.text) : { english: '', hindi: '' }

  if (loading) {
    return (
      <div className="page-section" style={{ marginTop: '70px' }}>
        <div className="container">
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !page) {
    return (
      <div className="page-section" style={{ marginTop: '150px' }}>
        <div className="container">
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <h1>Page Not Found</h1>
            <p>{error || 'The page you are looking for does not exist.'}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Search Bar */}
      <div className="search-section">
        <div className="container">
          <div className="page-search-container">
            <div className="search-box">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Search here..."
                value={searchQuery}
                onChange={handleSearch}
                onKeyPress={handleKeyPress}
                className="search-input"
              />
              {searchQuery && (
                <button 
                  className="search-clear"
                  onClick={() => {
                    setSearchQuery('')
                    setFilteredContents(contents)
                  }}
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <section className="page-section" style={{ marginTop: '0px' }}>
        <div className="container" style={{ maxWidth: '100%', padding: '0 10px' }}>
          <div className="page-content" style={{ 
            width: '100%', 
            backgroundColor: '#f8f9fa', 
            padding: '2rem 1rem', 
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <h1 className="page-title">{page.title}</h1>
            
            {/* Page Description Section */}
            {(pageContent.english || pageContent.hindi) && (
              <div className="page-description" style={{
                width: '100%',
                maxWidth: '100%',
                backgroundColor: '#ffffff',
                padding: '2rem',
                marginTop: '1.5rem',
                marginBottom: '1.5rem',
                marginLeft: '-1rem',
                marginRight: '-1rem',
                borderRadius: '6px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid #e9ecef'
              }}>
                {pageContent.english && (
                  <div className="page-text-english" style={{ marginBottom: pageContent.hindi ? '1rem' : '0' }}>
                    <p>{pageContent.english}</p>
                  </div>
                )}
                {pageContent.hindi && (
                  <div className="page-text-hindi">
                    <p>{pageContent.hindi}</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Fallback: If parsing didn't work, show original text */}
            {!pageContent.english && !pageContent.hindi && page.text && (
              <div 
                className="page-text" 
                style={{ 
                  width: '100%',
                  maxWidth: '100%',
                  backgroundColor: '#ffffff',
                  padding: '2rem',
                  marginTop: '1.5rem',
                  marginBottom: '1.5rem',
                  marginLeft: '-1rem',
                  marginRight: '-1rem',
                  borderRadius: '6px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  border: '1px solid #e9ecef',
                  lineHeight: '1.8',
                  fontSize: '1.1rem',
                  color: '#333'
                }}
                dangerouslySetInnerHTML={{ __html: page.text.replace(/\n/g, '<br />') }}
              />
            )}
            
            {/* Display Page Contents */}
            {filteredContents && filteredContents.length > 0 ? (
              <div style={{ marginTop: '3rem' }} ref={searchResultsRef}>
                <h2 style={{ marginBottom: '2rem', fontSize: '1.8rem', color: '#333' }}>
                  All {page.title}
                  {searchQuery && <span style={{ fontSize: '1rem', color: '#666' }}> ({filteredContents.length} results)</span>}
                </h2>
                <div className="content-list">
                  {filteredContents.map((content) => {
                    // Check link_type: if 'content', link to content display page
                    // if 'direct', link to external URL
                    const linkType = content.link_type || 'content'
                    const isContentPage = linkType === 'content'
                    const isDirectLink = linkType === 'direct' && content.link
                    const contentSlug = content.slug || slugify(content.title)
                    
                    return (
                      <div 
                        key={content.id} 
                        className="content-list-item"
                        ref={(el) => {
                          if (el) {
                            contentRefs.current[content.id] = el
                          }
                        }}
                      >
                        {isContentPage ? (
                          <Link 
                            to={`/${page.slug}/${contentSlug}`}
                            state={{ contentId: content.id }}
                            className="content-title-link"
                          >
                            {content.title}
                          </Link>
                        ) : isDirectLink ? (
                          <a 
                            href={content.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="content-title-link"
                          >
                            {content.title}
                          </a>
                        ) : (
                          <span className="content-title-link" style={{ cursor: 'default' }}>
                            {content.title}
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : contents && contents.length > 0 ? (
              <div style={{ marginTop: '3rem', textAlign: 'center', padding: '2rem' }}>
                <p style={{ fontSize: '1.1rem', color: '#666' }}>No results found for "{searchQuery}"</p>
              </div>
            ) : null}
            
            {page.link && (
              <div style={{ marginTop: '2rem' }}>
                <a 
                  href={page.link} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="btn btn-primary"
                  style={{ display: 'inline-block' }}
                >
                  Visit Link <i className="fas fa-external-link-alt"></i>
                </a>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  )
}

export default DynamicPage

